import { memo, useMemo, type FC } from 'react';
import { navigationHelper } from 'NavigationHelpers';
import { useSuspenseFeatureGate } from 'FrontRoyalStatsig';
import { VoiceAgentRoom } from 'TutorBotVoiceAgent';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import 'TutorBotVoiceAgent/locales/TutorBotVoiceAgent/TutorBotVoiceAgent-en.json';
import { useTargetBrandConfig, useCurrentUser } from 'FrontRoyalAngular';
import { UI_CONTEXT_COACH_AUDIO } from 'TutorBotConversation';
import { getProgramApplication } from 'Users';
import { type ProgramApplication } from 'ProgramApplication';
import './locales/TutorBotCoach/CoachPage-en.json';
import './locales/TutorBotCoach/CoachIntro-en.json';

// The max width is set to be a reasonable reading width, since messages stretch all the way
// from one side to the other. We assume that the max reasonable reading width is our small
// breakpoint, 768px.
const MAX_WIDTH = 'max-w-[768px]';

const keepTheMomentumLocales = [
    { earlyAdmission: true, declinedScholarship: false, localeKey: 'keepMomentumScholarshipAndEarlyAdmission' },
    { earlyAdmission: true, declinedScholarship: true, localeKey: 'keepMomentumEarlyAdmission' },
    { earlyAdmission: false, declinedScholarship: false, localeKey: 'keepMomentumScholarship' },
    { earlyAdmission: false, declinedScholarship: true, localeKey: 'keepMomentumNoScholarshipOrEarlyAdmission' },
];

function useKeepTheMomentum(programApplication: ProgramApplication | null) {
    const targetBrandConfig = useTargetBrandConfig();
    const { t } = useTranslation('back_royal', { keyPrefix: 'TutorBotCoach.CoachPage' });
    const keepTheMomentum = useMemo(() => {
        if (!programApplication) return '';
        let localeKey = keepTheMomentumLocales.find(
            ({ earlyAdmission, declinedScholarship }) =>
                earlyAdmission === (programApplication.earlyAdmissionOptIn ?? false) &&
                declinedScholarship === (programApplication.declineScholarshipConsideration ?? false),
        )?.localeKey;

        localeKey ||= 'keepMomentumNoScholarshipOrEarlyAdmission';

        return t(localeKey, { brandName: targetBrandConfig.brandNameShort });
    }, [programApplication, t, targetBrandConfig.brandNameShort]);

    return keepTheMomentum;
}

export const CoachPagePresentation: FC<{ goHome: () => void; programApplication: ProgramApplication }> = ({
    goHome,
    programApplication,
}) => {
    // Using two different locale files here. Putting the more generic stuff into
    // TutorBotVoiceAgent where it could be re-used, and the stuff that is specific
    // to coaching here.
    const { t } = useTranslation('back_royal');
    const { t: tIntro } = useTranslation('back_royal', {
        keyPrefix: 'TutorBotCoach.CoachIntro',
    });
    const brandName = useTargetBrandConfig().brandNameShort;

    const keepTheMomentum = useKeepTheMomentum(programApplication);

    const introMessages = useMemo(
        () => [tIntro('join_a_session', { brandName }), tIntro('it_is_a_chance_to'), tIntro('find_a_quiet_spot')],
        [tIntro, brandName],
    );

    // Because this page is rendered without the app shell, it needs to be absolute in order
    // to make it stretch the full height of the page.
    return (
        <div className={twMerge([MAX_WIDTH, 'mx-auto flex h-screen w-full items-center justify-center px-2.5'])}>
            <VoiceAgentRoom
                data-testid="voice-agent-room"
                agentIdentifier="coach"
                extraTokenAttributes={{ programApplicationId: programApplication?.id }}
                uiContext={UI_CONTEXT_COACH_AUDIO}
                color="purple"
                cardHeader={tIntro('thank_you_for_applying')!}
                cardTexts={[tIntro('we_have_received_your_submission', { brandName })!]}
                introMessages={introMessages}
                startDiscussionText={t('TutorBotCoach.CoachPage.startCoachingSession')}
                abortText={t('TutorBotVoiceAgent.TutorBotVoiceAgent.doThisLater')}
                abortFromRoom={goHome}
                allowAbortDuringConversation={false}
                postDiscussionButtonConfig={{
                    buttonText: t('TutorBotCoach.CoachPage.goToDashboard'),
                    action: goHome,
                }}
                postDiscussionCard={{
                    headerText: t('TutorBotCoach.CoachPage.yourCoachingSessionIsComplete'),
                    bodyParagraphs: [keepTheMomentum, t('TutorBotCoach.CoachPage.youCanExplore')],
                }}
            />
        </div>
    );
};

const CoachPageComponent: FC = () => {
    const currentUser = useCurrentUser();
    const programApplication = getProgramApplication(currentUser);
    const coachEnabled = useSuspenseFeatureGate('voice_coach');
    const { goHome } = navigationHelper();

    if (!coachEnabled || !programApplication) {
        goHome();
        return null;
    }

    return <CoachPagePresentation goHome={goHome} programApplication={programApplication} />;
};

export const CoachPage = memo(CoachPageComponent) as typeof CoachPageComponent;
